// orchestrator_fullstack/frontend/src/app/admin/AdminLayout.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useCan } from "../auth/useCan";
import {
  Activity,
  AppWindow,
  Bot,
  Cpu,
  ChevronLeft,
  ChevronRight,
  FileText,
  GitBranch,
  Inbox,
  LayoutDashboard,
  Link as LinkIcon,
  LogOut,
  MessageSquare,
  Server,
  Settings,
  Shield,
  Sparkles,
  Workflow,
} from "lucide-react";

/**
 * AdminLayout (drop-in upgrade)
 * - Preserves your existing IA + routes (overview, workflow, providers, ingress/*, observability/*, governance/*)
 * - Keeps RELATIVE routing (AdminRouter mounts at /admin/* or /app/admin/*)
 * - Keeps your existing permission gating via useCan(permission)
 * - Modern shell: collapsible sidebar, icon nav, topbar with tenant/roles + sign out
 * - Adds Settings (top-right) dropdown:
 *     - "Users & Roles" (admin + platform-admin)
 *     - "Tenants & OIDC" (platform-admin only)
 * - Does NOT rely on X-Tenant-ID for admin (admin is JWT-only per Phase 1 hardening)
 */
export default function AdminLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { state, signOut } = useAuth();

  // useCan() should return a function; guard in case something unexpected happens
  const canHook = useCan();
  const can = useMemo(() => {
    if (typeof canHook === "function") return canHook as (p: any) => boolean;
    return (_p: any) => false;
  }, [canHook]);

  // Sidebar collapse persisted (safe)
  const LS_KEY = "orchestrator.sidebarCollapsed";
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, collapsed ? "1" : "0");
    } catch {
      // ignore
    }
  }, [collapsed]);
  const toggleCollapsed = useCallback(() => setCollapsed((v) => !v), []);

  // Works whether the router is mounted at /admin/* or /app/admin/* (or others)
  // by detecting the current mount prefix dynamically.
  const mountBase = useMemo(() => {
    const p = loc.pathname || "";
    if (p.startsWith("/app/admin")) return "/app/admin";
    if (p.startsWith("/admin")) return "/admin";
    // fallback
    return "/admin";
  }, [loc.pathname]);

  const isActive = useCallback(
    (relTo: string) => {
      const base = mountBase;
      const t = relTo.replace(/^\/+/, "");
      const target = t ? `${base}/${t}` : base;
      return loc.pathname === target || loc.pathname.startsWith(target + "/");
    },
    [loc.pathname, mountBase]
  );

  const cxTab = useCallback(
    (relTo: string) => {
      const active = isActive(relTo);
      return [
        "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition",
        active ? "bg-zinc-200 text-zinc-900" : "text-zinc-700 hover:bg-zinc-100",
      ].join(" ");
    },
    [isActive]
  );

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mt-4 mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
      {children}
    </div>
  );

  const Tab = ({
    to,
    label,
    icon,
  }: {
    to: string; // relative
    label: string;
    icon: React.ReactNode;
  }) => (
    <Link to={to} className={cxTab(to)} title={collapsed ? label : undefined}>
      <span className="h-5 w-5 flex items-center justify-center text-zinc-700">{icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
    </Link>
  );

  // -------------------------
  // Permission gating (your map)
  // -------------------------
  // Core
  const canOverview = can("admin.overview.view");
  const canWorkflow = can("flows.edit"); // tenant-admin/admin + platform-admin
  const canProviders = can("admin.providers.view"); // platform-admin only
  const canRuns = can("runs.view");

  // Ingress
  const canIngressAppsView = can("ingress.apps.view");
  const canIngressAppsManage = can("ingress.apps.manage");
  const canIngressSubmit = can("ingress.submit");
  const canThreadsView = can("threads.view");

  // Observability
  const canObs = can("admin.observability.view");

  // Governance
  //const canPoliciesView = can("admin.policies.view");
  //const canGovernanceView = can("admin.governance.view");

  const isPlatformAdmin = (state.roles || []).includes("platform-admin");

  const canPoliciesView = isPlatformAdmin;       // match route gate
  const canGovernanceView = isPlatformAdmin;     // match route gate
  const showCore = canOverview || canWorkflow || canProviders || canRuns;
  const showIngress = canIngressAppsView || canIngressAppsManage || canIngressSubmit || canThreadsView;
  const showIntelligence = canObs;
  const showGovernance = canPoliciesView || canGovernanceView;

  // Topbar info
  const tenantLabel = state.tenant || "(unset)";
  const rolesLabel = state.roles?.length ? state.roles.join(", ") : "(none)";

  // -------------------------
  // Settings dropdown (Option A)
  // -------------------------
  const canUsersRoles =
    // permission-based (preferred if present)
    can("admin.access") ||
    // role-based fallback (defensive)
    (state.roles || []).includes("admin") ||
    (state.roles || []).includes("platform-admin");

  const canTenantsOidc =
    can("admin.oidc.view") ||
    can("admin.oidc.manage") ||
    (state.roles || []).includes("platform-admin");

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsBtnRef = useRef<HTMLButtonElement | null>(null);
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!settingsOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    const onPointerDown = (e: MouseEvent | PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (settingsBtnRef.current?.contains(t)) return;
      if (settingsMenuRef.current?.contains(t)) return;
      setSettingsOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [settingsOpen]);

  const settingsItems = useMemo(() => {
    const items: Array<{
      key: string;
      label: string;
      to: string; // absolute
      show: boolean;
      desc?: string;
    }> = [
      {
        key: "users_roles",
        label: "Users & Roles",
        to: `${mountBase}/oidc/users-roles`,
        show: canUsersRoles,
        desc: "Email & group role mappings for this tenant",
      },
      {
        key: "users_active",
        label: "Active Users",
        to: `${mountBase}/oidc/users-active`,
        show: (state.roles || []).includes("platform-admin"),
        desc: "Provisioned identities: who logged in and derived roles",
      },
      {
        key: "tenants_oidc",
        label: "Tenants & OIDC",
        to: `${mountBase}/oidc/tenants-oidc`,
        show: canTenantsOidc,
        desc: "Tenants, group→tenant and group→role mappings",
      },
    ];
    return items.filter((x) => x.show);
  }, [canUsersRoles, canTenantsOidc, mountBase]);

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside
        className={[
          "sticky top-0 h-screen border-r border-zinc-200 bg-white",
          collapsed ? "w-[76px]" : "w-[252px]",
        ].join(" ")}
      >
        {/* Make sidebar a flex column so nav can scroll */}
        <div className="h-full flex flex-col p-3">
          {/* Brand + collapse (fixed height) */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 select-none">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-zinc-900 to-zinc-700 text-white flex items-center justify-center shadow-sm">
                <span className="text-sm font-extrabold">1-i</span>
              </div>
              {!collapsed && (
                <div className="leading-tight">
                  <div className="text-sm font-extrabold tracking-tight text-zinc-900">One Intelligence</div>
                  <div className="text-[11px] text-zinc-500">Admin</div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={toggleCollapsed}
              className="h-9 w-9 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 flex items-center justify-center"
              title={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>

          {/* Nav: scrollable */}
          <nav className="mt-3 flex-1 overflow-y-auto pr-1 flex flex-col gap-1">
            {showCore && (
              <>
                {!collapsed && <SectionTitle>Core</SectionTitle>}
                {canOverview && <Tab to="overview" label="Overview" icon={<LayoutDashboard className="h-4 w-4" />} />}
                {canWorkflow && <Tab to="workflow" label="Workflow" icon={<Workflow className="h-4 w-4" />} />}
                {canProviders && <Tab to="providers" label="Providers" icon={<Bot className="h-4 w-4" />} />}
                {canRuns && <Tab to="runs" label="Runs" icon={<Inbox className="h-4 w-4" />} />}
              </>
            )}

            {showIngress && (
              <>
                {!collapsed && <SectionTitle>Ingress</SectionTitle>}
                {(canIngressAppsView || canIngressAppsManage) && (
                  <Tab to="ingress/apps" label="Apps" icon={<AppWindow className="h-4 w-4" />} />
                )}
                {canRuns && <Tab to="ingress/requests" label="Requests" icon={<Inbox className="h-4 w-4" />} />}
                {canThreadsView && (
                  <Tab to="ingress/feedback" label="Feedback" icon={<MessageSquare className="h-4 w-4" />} />
                )}
                {canIngressAppsManage && (
                  <Tab to="ingress/starter-apps" label="Starter Apps" icon={<Sparkles className="h-4 w-4" />} />
                )}
              </>
            )}

            {showIntelligence && (
              <>
                {!collapsed && <SectionTitle>Intelligence</SectionTitle>}
                <Tab to="observability/platform" label="Platform" icon={<Activity className="h-4 w-4" />} />
                <Tab to="observability/providers" label="Provider Health" icon={<Cpu className="h-4 w-4" />} />
                <Tab to="observability/flows" label="Flows" icon={<GitBranch className="h-4 w-4" />} />
                <Tab to="observability/policy-feedback" label="Policy-Feedback" icon={<Server className="h-4 w-4" />} />
              </>
            )}

            {showGovernance && (
              <>
                {!collapsed && <SectionTitle>Governance</SectionTitle>}
                {canPoliciesView && <Tab to="governance/policies" label="Policies" icon={<Shield className="h-4 w-4" />} />}
                {canGovernanceView && <Tab to="governance/bindings" label="Bindings" icon={<LinkIcon className="h-4 w-4" />} />}
                {canGovernanceView && <Tab to="governance/decisions" label="Decision Log" icon={<FileText className="h-4 w-4" />} />}
              </>
            )}
          </nav>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Topbar */}
        <div className="sticky top-0 z-20 border-b border-zinc-200 bg-white/80 backdrop-blur">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-zinc-500 truncate">
                Tenant: <span className="font-semibold text-zinc-800">{tenantLabel}</span> · Roles:{" "}
                <span className="font-semibold text-zinc-800">{rolesLabel}</span>
              </div>
              <div className="mt-1 text-sm font-extrabold text-zinc-900 truncate">Admin Console</div>
            </div>

            <div className="flex items-center gap-2">
              {/* Settings dropdown */}
              <div className="relative">
                <button
                  ref={settingsBtnRef}
                  type="button"
                  className={[
                    "inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-semibold text-zinc-800 shadow-sm hover:bg-zinc-50",
                    settingsItems.length === 0 ? "opacity-50 cursor-not-allowed" : "",
                  ].join(" ")}
                  onClick={() => {
                    if (settingsItems.length === 0) return;
                    setSettingsOpen((v) => !v);
                  }}
                  title={settingsItems.length === 0 ? "No settings available for your role" : "Settings"}
                  aria-haspopup="menu"
                  aria-expanded={settingsOpen ? "true" : "false"}
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">Settings</span>
                </button>

                {settingsOpen && settingsItems.length > 0 && (
                  <div
                    ref={settingsMenuRef}
                    role="menu"
                    aria-label="Settings menu"
                    className="absolute right-0 mt-2 w-[320px] rounded-2xl border border-zinc-200 bg-white shadow-lg overflow-hidden"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100">
                      <div className="text-xs font-semibold text-zinc-700">Admin Tools</div>
                      <div className="text-[11px] text-zinc-500">Identity, tenants, access mappings</div>
                    </div>

                    <div className="p-2">
                      {settingsItems.map((it) => (
                        <button
                          key={it.key}
                          type="button"
                          role="menuitem"
                          className="w-full text-left rounded-xl px-3 py-2 hover:bg-zinc-50 transition"
                          onClick={() => {
                            setSettingsOpen(false);
                            nav(it.to);
                          }}
                        >
                          <div className="text-sm font-semibold text-zinc-900">{it.label}</div>
                          {it.desc ? <div className="text-[12px] text-zinc-500">{it.desc}</div> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sign out */}
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-semibold text-zinc-800 shadow-sm hover:bg-zinc-50"
                onClick={() => {
                  signOut();
                  nav("/login", { replace: true });
                }}
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <main className="mx-auto max-w-[1400px] px-4 sm:px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}